// The import popup, one for every dataset on the Import Data screen.
//
// Same four beats as the customer bulk import (Pawn Customer Master > Bulk
// Create), because that is the flow the shops have already been taught:
//
//   1 · get the format right   — download the template built from the spec
//   2 · upload the filled sheet
//   3 · check what was read    — counts, then row by row, before anything moves
//   4 · import                 — nothing is created until this is pressed
//
// Steps 1-3 are spec-driven and need nothing but the file itself. Step 4 is
// where the app gets involved: ./importRunner matches every name in the sheet
// against this store's masters and sends the rows up in chunks, and the result
// comes back row by row. A spec whose importer is not written yet
// (status: "draft") says so in plain words instead of offering a button that
// would fail.
//
// "Check first" runs that entire path with dry_run set — every lookup, every
// duplicate check, every balance test — and creates nothing. It is the
// rehearsal, and for a migration that lands in the ledger it is worth insisting
// on before the real thing.
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import PlaylistAddCheckRoundedIcon from "@mui/icons-material/PlaylistAddCheckRounded";
import ConstructionRoundedIcon from "@mui/icons-material/ConstructionRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import SubdirectoryArrowRightRoundedIcon from "@mui/icons-material/SubdirectoryArrowRightRounded";

import {
  DATA_SHEET_NAME,
  buildRows,
  downloadProblemReport,
  downloadTemplate,
  extractRows,
  mandatoryCount,
  readWorkbook,
  summarise,
} from "./importSheet";
import {
  checkReadiness,
  datesOf,
  loadMasters,
  resolveAll,
  resyncSequences,
  runImport,
} from "./importRunner";
import { downloadOutcomeReport } from "./importReport";

const BLUE = "#2563EB";
const INK = "#0F172A";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";

// How many rows the pre-flight table shows before it stops. A 4000-row migration
// sheet is normal and rendering all of it would freeze the dialog; the counts
// above the table always cover the whole file, and every problem row is listed
// in full regardless of this cap.
const PREVIEW_LIMIT = 60;

// ─── Counter tile ─────────────────────────────────────────────────────────────
const StatTile = ({ label, value, tone = "slate", icon }) => {
  const tones = {
    slate: { fg: "#334155", bg: "#F1F5F9", br: BORDER },
    blue: { fg: BLUE, bg: `${BLUE}12`, br: `${BLUE}33` },
    green: { fg: "#15803D", bg: "#ECFDF5", br: "#A7F3D0" },
    red: { fg: "#B91C1C", bg: "#FEF2F2", br: "#FECACA" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <Box
      sx={{
        flex: "1 1 110px",
        minWidth: 104,
        px: 1.25,
        py: 1,
        borderRadius: 2.5,
        bgcolor: t.bg,
        border: `1px solid ${t.br}`,
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center">
        {icon
          ? React.cloneElement(icon, { sx: { fontSize: 15, color: t.fg } })
          : null}
        <Typography
          sx={{
            fontSize: "0.66rem",
            fontWeight: 700,
            color: t.fg,
            letterSpacing: 0.3,
          }}
        >
          {label.toUpperCase()}
        </Typography>
      </Stack>
      <Typography
        sx={{
          fontSize: "1.25rem",
          fontWeight: 800,
          color: t.fg,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const StepHeading = ({ n, children }) => (
  <Typography
    variant="subtitle2"
    sx={{ fontWeight: 700, color: BLUE, mb: 0.75 }}
  >
    Step {n} · {children}
  </Typography>
);

const ImportSheetDialog = ({
  open,
  spec,
  onClose,
  storeName,
  isUnofficial = false,
  storeId,
  userId,
  pawnMode = 1,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const fileInputRef = useRef(null);
  const resultRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [parseError, setParseError] = useState("");
  const [parsed, setParsed] = useState(null); // { sheetName, matchedByHeader, rows }
  const [showAllColumns, setShowAllColumns] = useState(false);

  // ── Step 4 ───────────────────────────────────────────────────────────────
  // phase: idle → resolving (matching names to the app's masters) → running →
  // done. `dryRun` remembers whether the last run created anything, because a
  // check and an import produce the same-looking report and confusing the two
  // would be the worst kind of mistake here.
  const [phase, setPhase] = useState("idle");
  const [runError, setRunError] = useState("");
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [run, setRun] = useState(null); // { summary, results, dryRun, unresolved }
  const stopRef = useRef(false);

  const isDraft = spec?.status === "draft";
  // A master sheet (Accounts) describes the store, not a transaction in one of
  // its books. It resolves nothing against the masters, posts nothing to the
  // ledger, moves no voucher counter, and — because account_master has no
  // status column — is not official/unofficial at all. Saying "official book"
  // over an accounts import would be a plain untruth, so the mode is hidden.
  const isMaster = spec?.kind === "master";
  const busy = phase === "resolving" || phase === "running";

  const summary = useMemo(
    () => (parsed && spec ? summarise(parsed.rows, spec) : null),
    [parsed, spec]
  );

  // The columns the pre-flight table shows: the ones the spec flagged as worth
  // seeing, unless the reader asked for everything.
  const previewColumns = useMemo(() => {
    if (!spec) return [];
    if (showAllColumns) return spec.columns;
    const flagged = spec.columns.filter((c) => c.preview);
    return flagged.length ? flagged : spec.columns.slice(0, 6);
  }, [spec, showAllColumns]);

  const problemRows = useMemo(
    () => (parsed ? parsed.rows.filter((r) => r.problems.length) : []),
    [parsed]
  );

  // Steps 1 and 2 fill the paper, so the verdict on a freshly read sheet lands
  // below the fold — the one thing the reader is waiting for. Bring it up.
  useEffect(() => {
    if (parsed)
      resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [parsed]);

  const resetAll = () => {
    setFileName("");
    setParsed(null);
    setParseError("");
    setShowAllColumns(false);
    setPhase("idle");
    setRun(null);
    setRunError("");
    setProgress({ done: 0, total: 0 });
    stopRef.current = false;
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── The run itself ─────────────────────────────────────────────────────────
  // Three things happen in order, and each can stop the import with something
  // the operator can act on:
  //   1 · resolve every name in the sheet against this store's masters
  //   2 · ask the server whether it can take these dates at all (posting
  //       accounts set, a financial year covering every one of them)
  //   3 · send the rows up in chunks
  // A dry run does all three and creates nothing — the safe rehearsal a shop
  // should always do before the real thing.
  const execute = async (dryRun) => {
    if (!spec || !parsed) return;
    stopRef.current = false;
    setRunError("");
    setRun(null);
    setPhase("resolving");

    try {
      const clean = parsed.rows.filter((r) => !r.problems.length);

      // A master sheet names nothing outside itself, so there is nothing to
      // resolve it against — and loading the masters anyway would page every
      // customer in the store into the browser to answer no question at all.
      const masters = isMaster ? {} : await loadMasters(storeId);
      const { ready, blocked } = resolveAll(clean, spec, masters, { pawnMode });

      // A row whose customer or scheme could not be matched never reaches the
      // server, so it would otherwise be missing from the report entirely — the
      // one place the operator looks to find out what to fix. Give each one the
      // same shape a server verdict has, and they travel together from here on.
      const blockedResults = blocked.map((b) => ({
        row_no: b.rowNo,
        voucher_no: b.group || null,
        outcome: "failed",
        message: b.problems.join(" | "),
        warnings: [],
      }));

      if (!ready.length) {
        setPhase("idle");
        setRunError(
          blocked.length
            ? `Nothing in this sheet could be matched to this store. ${blocked[0].problems[0]}`
            : "There is nothing left to import in this sheet."
        );
        if (blockedResults.length)
          setRun({
            results: blockedResults,
            created: 0,
            ready: 0,
            skipped: 0,
            failed: blockedResults.length,
            warned: 0,
            dryRun,
            moved: [],
          });
        return;
      }

      // Accounts skip this, and must: the readiness check demands that Pawn
      // Settings already name a pledge / cash / interest account, which is
      // exactly what a shop cannot do until its accounts exist. Checking it
      // here would make the accounts sheet impossible to run on a fresh store —
      // the one store that needs it most.
      if (!isMaster) {
        const readiness = await checkReadiness({
          storeId,
          dates: datesOf(ready),
        });
        if (!readiness.ready) {
          setPhase("idle");
          setRunError(readiness.blockers.join(" "));
          return;
        }
      }

      setPhase("running");
      setProgress({ done: 0, total: ready.length });

      const summary = await runImport({
        spec,
        ready,
        storeId,
        userId,
        pawnMode,
        dryRun,
        onProgress: ({ done, total }) => setProgress({ done, total }),
        shouldStop: () => stopRef.current,
      });

      // Imported vouchers carry their own numbers, so the store's counters know
      // nothing about them until they are told — otherwise the next ticket
      // written at the counter collides with a loan from years ago.
      // Accounts carry no voucher number, so there is no counter to move.
      let moved = [];
      if (!isMaster && !dryRun && summary.created > 0) {
        try {
          moved = await resyncSequences({ storeId, pawnMode });
        } catch {
          // Never fail an import over this: it is repairable at any time by
          // running the import again, and the rows themselves are already in.
          moved = [];
        }
      }

      setRun({
        ...summary,
        // Unmatched rows count as failures and sit in the report in sheet
        // order, so "not imported" is the whole truth and the report can be
        // read straight down beside the spreadsheet it came from.
        results: [...summary.results, ...blockedResults].sort(
          (a, b) => (a.row_no ?? 0) - (b.row_no ?? 0)
        ),
        failed: summary.failed + blockedResults.length,
        dryRun,
        moved,
      });
      setPhase("done");
    } catch (error) {
      setPhase("idle");
      setRunError(
        error?.response?.data?.statusMessage ||
          error?.message ||
          "Something went wrong before anything was created."
      );
    }
  };

  const handleClose = () => {
    resetAll();
    onClose?.();
  };

  const ingest = async (file) => {
    if (!file || !spec) return;

    setFileName(file.name);
    setParseError("");
    setParsed(null);
    setIsParsing(true);

    try {
      const workbook = await readWorkbook(file);
      const extracted = extractRows(workbook, spec);

      if (!extracted.dataRows.length) {
        setParseError(
          `No rows found in "${file.name}". Fill your rows into the "${DATA_SHEET_NAME}" sheet of the template and upload it again. (The "Sample Data" sheet is never imported.)`
        );
        return;
      }

      setParsed({
        sheetName: extracted.sheetName,
        matchedByHeader: !!extracted.headerIndexes,
        rows: buildRows(extracted, spec),
      });
    } catch (err) {
      console.error("Import sheet parse failed:", err);
      setParseError(
        "That file could not be opened as a spreadsheet. Save it as .xlsx or .csv and try again."
      );
    } finally {
      setIsParsing(false);
    }
  };

  const handleFilePicked = (event) => ingest(event.target.files?.[0]);

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    if (!isParsing) ingest(event.dataTransfer?.files?.[0]);
  };

  if (!spec) return null;

  // What a pre-flight cell shows. A value that could not be read comes back as
  // null, and printing "—" there would hide the very thing the reader has to
  // correct — so the raw text they typed is shown instead. Dates are shown the
  // way an Indian shop writes them, not as the ISO string we store.
  const cellText = (row, col) => {
    const v = row.values[col.key];
    if (v === true) return "Yes";
    if (v === false) return "No";
    if (v === null || v === "") return row.raw?.[col.key] || "—";
    if (col.type === "date") {
      const [y, m, d] = String(v).split("-");
      return d ? `${d}/${m}/${y}` : String(v);
    }
    return String(v);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          background:
            "linear-gradient(90deg, #1D4ED8 0%, #2563EB 50%, #3B82F6 100%)",
          color: "#fff",
          py: 1.75,
        }}
      >
        <UploadFileRoundedIcon />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* component="div": DialogTitle is an <h2>, which may not carry the
              <h6> that variant="subtitle1" would render. */}
          <Typography
            variant="subtitle1"
            component="div"
            fontWeight={700}
            noWrap
          >
            Import {spec.title}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
            {storeName ? `Into ${storeName}` : "Data migration"}
            {isMaster
              ? ""
              : ` · ${isUnofficial ? "unofficial book" : "official book"}`}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: "#fff" }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "#F8FAFC" }}>
        {/* The book an import lands in is the mode the screen is in — and that
            is invisible unless somebody says so. */}
        {isUnofficial && !isMaster && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            You are in the <b>unofficial</b> book. Everything imported here
            lands there, not in the official one.
          </Alert>
        )}

        {/* The accounts sheet's one real consequence, said before the import
            rather than discovered in a report afterwards. */}
        {isMaster && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            These accounts are shared with the Accounts module, and all of them
            arrive under the <b>Income</b> category with no group. Set their
            real categories on the Accounts screen afterwards — until then the
            Trial Balance and Profit &amp; Loss will read wrongly. Your loans,
            receipts and ledger postings are not affected.
          </Alert>
        )}

        {/* ── Step 1 · template ── */}
        <Box sx={{ mb: 2.5 }}>
          <StepHeading n={1}>Get the format right</StepHeading>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Typography
              variant="body2"
              sx={{ flex: 1, color: "text.secondary" }}
            >
              Download the template — it lists every column, what to fill in it,
              whether it is mandatory and where the value ends up. One row ={" "}
              <b>{spec.rowMeaning}</b>. Its <b>Sample Data</b> sheet has a
              worked example; type your own into <b>{DATA_SHEET_NAME}</b>.
              {spec.dependsOn?.length ? (
                <>
                  {" "}
                  Import <b>{spec.dependsOn.join(", ")}</b> before this sheet.
                </>
              ) : null}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadRoundedIcon />}
              onClick={() =>
                spec.downloadTemplate
                  ? spec.downloadTemplate()
                  : downloadTemplate(spec)
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                whiteSpace: "nowrap",
                borderColor: BLUE,
                color: BLUE,
              }}
            >
              Download template
            </Button>
          </Stack>
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ mt: 1 }}
            flexWrap="wrap"
            useFlexGap
          >
            <Chip
              size="small"
              variant="outlined"
              label={`${spec.columns.length} columns`}
              sx={{ height: 22, fontSize: "0.68rem" }}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`${mandatoryCount(spec)} mandatory`}
              sx={{ height: 22, fontSize: "0.68rem" }}
            />
            {spec.groupBy ? (
              <Chip
                size="small"
                variant="outlined"
                label={`Several rows may make one ${spec.groupNoun}`}
                sx={{ height: 22, fontSize: "0.68rem" }}
              />
            ) : null}
          </Stack>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* ── Step 2 · upload ── */}
        <Box sx={{ mb: 2 }}>
          <StepHeading n={2}>Upload the filled sheet</StepHeading>

          <input
            type="file"
            ref={fileInputRef}
            accept=".xlsx,.xls,.csv"
            hidden
            onChange={handleFilePicked}
          />

          <Box
            onClick={() => {
              if (!isParsing) fileInputRef.current?.click();
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            sx={{
              display: "block",
              border: `2px dashed ${parsed || dragOver ? BLUE : "#94A3B8"}`,
              borderRadius: 3,
              p: { xs: 2.5, md: 3.5 },
              textAlign: "center",
              bgcolor: parsed || dragOver ? `${BLUE}08` : "#fff",
              cursor: isParsing ? "not-allowed" : "pointer",
              transition: "all 0.2s ease",
              "&:hover": { bgcolor: isParsing ? undefined : `${BLUE}0A` },
            }}
          >
            <UploadFileRoundedIcon
              sx={{ color: BLUE, fontSize: 34, mb: 0.5 }}
            />
            <Typography variant="body2" fontWeight={600} sx={{ color: BLUE }}>
              {fileName || "Click here — or drop your Excel file on this box"}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              .xlsx, .xls or .csv — no need to convert to CSV first
            </Typography>
          </Box>

          {(fileName || parsed) && (
            <Button
              size="small"
              color="error"
              startIcon={<DeleteOutlineRoundedIcon />}
              onClick={resetAll}
              sx={{ textTransform: "none", mt: 1 }}
            >
              Remove file
            </Button>
          )}
        </Box>

        {isParsing && (
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <CircularProgress size={18} />
            <Typography variant="body2">Reading the sheet…</Typography>
          </Stack>
        )}

        {parseError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {parseError}
          </Alert>
        )}

        {/* ── Step 3 · pre-flight ── */}
        {parsed && summary && (
          <Box ref={resultRef} sx={{ scrollMarginTop: 8 }}>
            <StepHeading n={3}>Check what was read</StepHeading>

            {/* component="div": the chip below is a <div>, and a <p> may not
                carry one — React warns about the nesting otherwise. */}
            <Typography variant="body2" component="div" sx={{ mb: 1.25 }}>
              <b>{summary.total}</b> row{summary.total === 1 ? "" : "s"} read
              from the <b>{parsed.sheetName}</b> sheet
              {spec.groupBy ? (
                <>
                  {" "}
                  — <b>{summary.groups}</b> {spec.groupNoun}
                  {summary.groups === 1 ? "" : "s"} in all
                </>
              ) : null}
              .
              <Tooltip
                title={
                  parsed.matchedByHeader
                    ? "The header row was recognised, so columns were matched by their names."
                    : `No usable header row was found, so columns were read strictly by position A to ${
                        spec.columns[spec.columns.length - 1].letter
                      }.`
                }
              >
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    parsed.matchedByHeader
                      ? "Matched by header name"
                      : `Read by position (A–${
                          spec.columns[spec.columns.length - 1].letter
                        })`
                  }
                  sx={{ ml: 1, height: 20, fontSize: "0.68rem" }}
                />
              </Tooltip>
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 1.5 }}
              flexWrap="wrap"
              useFlexGap
            >
              <StatTile
                label="Rows read"
                value={summary.total}
                tone="blue"
                icon={<PlaylistAddCheckRoundedIcon />}
              />
              {spec.groupBy ? (
                <StatTile
                  label={`${spec.groupNoun}s`}
                  value={summary.groups}
                  tone="slate"
                />
              ) : null}
              <StatTile
                label="Ready"
                value={summary.ready}
                tone="green"
                icon={<CheckCircleRoundedIcon />}
              />
              <StatTile
                label="To fix"
                value={summary.blocked}
                tone={summary.blocked ? "red" : "slate"}
                icon={<ErrorOutlineRoundedIcon />}
              />
            </Stack>

            {summary.blocked > 0 && (
              <Alert
                severity="warning"
                sx={{ mb: 1.5, borderRadius: 2 }}
                action={
                  <Button
                    size="small"
                    onClick={() => downloadProblemReport(problemRows, spec)}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Download rows to fix
                  </Button>
                }
              >
                <AlertTitle
                  sx={{ fontSize: "0.85rem", fontWeight: 700, mb: 0.25 }}
                >
                  {summary.blocked} row{summary.blocked === 1 ? "" : "s"} cannot
                  be imported yet
                </AlertTitle>
                Each one is marked in the table below. Fix them in your sheet
                and upload it again — the rest are fine.
              </Alert>
            )}

            {/* Row-by-row. Problem rows first: they are the reason anyone reads
                this table, and a 500-row sheet would otherwise bury them. */}
            <Box
              sx={{
                bgcolor: "#fff",
                border: `1px solid ${BORDER}`,
                borderRadius: 2.5,
                overflow: "hidden",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${BORDER}` }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    color: INK,
                  }}
                >
                  Row by row
                  {parsed.rows.length > PREVIEW_LIMIT ? (
                    <Box
                      component="span"
                      sx={{ fontWeight: 500, color: INK_MUTED }}
                    >
                      {" "}
                      — problem rows, then the first {PREVIEW_LIMIT}
                    </Box>
                  ) : null}
                </Typography>
                <Button
                  size="small"
                  startIcon={
                    showAllColumns ? (
                      <VisibilityOffRoundedIcon />
                    ) : (
                      <VisibilityRoundedIcon />
                    )
                  }
                  onClick={() => setShowAllColumns((v) => !v)}
                  sx={{ textTransform: "none", fontWeight: 600, color: BLUE }}
                >
                  {showAllColumns ? "Key columns" : "All columns"}
                </Button>
              </Stack>

              <Box sx={{ maxHeight: 320, overflow: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: 700, fontSize: "0.72rem", width: 56 }}
                      >
                        Row
                      </TableCell>
                      {previewColumns.map((c) => (
                        <TableCell
                          key={c.key}
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {c.header}
                        </TableCell>
                      ))}
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem" }}>
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      ...problemRows,
                      ...parsed.rows
                        .filter((r) => !r.problems.length)
                        .slice(0, PREVIEW_LIMIT),
                    ].map((r) => (
                      <TableRow
                        key={r.rowNo}
                        sx={{
                          bgcolor: r.problems.length ? "#FEF2F2" : undefined,
                        }}
                      >
                        <TableCell
                          sx={{
                            fontSize: "0.72rem",
                            fontVariantNumeric: "tabular-nums",
                            color: INK_MUTED,
                          }}
                        >
                          {r.isContinuation ? (
                            <Tooltip
                              title={`Continues ${spec.groupNoun} ${r.group}`}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.25}
                              >
                                <SubdirectoryArrowRightRoundedIcon
                                  sx={{ fontSize: 13, color: INK_MUTED }}
                                />
                                <span>{r.rowNo}</span>
                              </Stack>
                            </Tooltip>
                          ) : (
                            r.rowNo
                          )}
                        </TableCell>
                        {previewColumns.map((c) => (
                          <TableCell
                            key={c.key}
                            sx={{
                              fontSize: "0.75rem",
                              whiteSpace: "nowrap",
                              maxWidth: 190,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              color:
                                r.isContinuation && c.scope === "record"
                                  ? INK_MUTED
                                  : INK,
                            }}
                          >
                            {cellText(r, c)}
                          </TableCell>
                        ))}
                        <TableCell sx={{ fontSize: "0.72rem", minWidth: 180 }}>
                          {r.problems.length ? (
                            <Tooltip title={r.problems.join(" · ")}>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                              >
                                <ErrorOutlineRoundedIcon
                                  sx={{ fontSize: 15, color: "#B91C1C" }}
                                />
                                <Box component="span" sx={{ color: "#B91C1C" }}>
                                  {r.problems[0]}
                                  {r.problems.length > 1
                                    ? ` +${r.problems.length - 1} more`
                                    : ""}
                                </Box>
                              </Stack>
                            </Tooltip>
                          ) : (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <CheckCircleRoundedIcon
                                sx={{ fontSize: 15, color: "#15803D" }}
                              />
                              <Box component="span" sx={{ color: "#15803D" }}>
                                Ready
                              </Box>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>

            {/* ── Step 4 · import ── */}
            <Box sx={{ mt: 2.5 }}>
              <StepHeading n={4}>Import</StepHeading>
              {isDraft ? (
                <Alert
                  severity="info"
                  icon={<ConstructionRoundedIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  <AlertTitle
                    sx={{ fontSize: "0.85rem", fontWeight: 700, mb: 0.25 }}
                  >
                    The {spec.label.toLowerCase()} importer is still being built
                  </AlertTitle>
                  Everything above is live, so you can get your sheet right now
                  and it will import unchanged the moment this is switched on —
                  the columns will not change under you. Nothing has been
                  created.
                </Alert>
              ) : (
                <>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {summary.ready} row{summary.ready === 1 ? "" : "s"} will be
                    imported. Nothing is created until you press Import, and
                    rows are created one at a time — if it is interrupted,
                    whatever was created stays created and running it again
                    picks up exactly where it stopped.
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mt: 0.75 }}
                  >
                    <b>Check first</b> runs the whole thing against the app{" "}
                    {isMaster
                      ? "— telling you which of these are already here, and which names clash with an account you already have —"
                      : "— matching every customer, scheme and floor, and proving the money adds up —"}{" "}
                    without creating anything. Always worth the minute.
                  </Typography>

                  {runError && (
                    <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
                      <AlertTitle
                        sx={{ fontSize: "0.85rem", fontWeight: 700, mb: 0.25 }}
                      >
                        Nothing was created
                      </AlertTitle>
                      {runError}
                    </Alert>
                  )}

                  {busy && (
                    <Box sx={{ mt: 1.75 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 0.75 }}
                      >
                        <CircularProgress size={15} thickness={5} />
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            color: INK,
                          }}
                        >
                          {phase === "resolving"
                            ? "Matching your sheet against this store…"
                            : `${progress.done} of ${progress.total} ${
                                spec.groupNoun || "row"
                              }s…`}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant={
                          phase === "running" && progress.total
                            ? "determinate"
                            : "indeterminate"
                        }
                        value={
                          progress.total
                            ? (progress.done / progress.total) * 100
                            : 0
                        }
                        sx={{ height: 7, borderRadius: 4 }}
                      />
                    </Box>
                  )}

                  {run && (
                    <Box sx={{ mt: 1.75 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ flexWrap: "wrap", gap: 1, mb: 1 }}
                      >
                        <StatTile
                          label={run.dryRun ? "Would import" : "Imported"}
                          value={run.dryRun ? run.ready : run.created}
                          tone="green"
                          icon={<CheckCircleRoundedIcon />}
                        />
                        <StatTile
                          label="Already there"
                          value={run.skipped}
                          tone="slate"
                        />
                        <StatTile
                          label="Not imported"
                          value={run.failed}
                          tone={run.failed ? "red" : "slate"}
                          icon={<ErrorOutlineRoundedIcon />}
                        />
                        <StatTile
                          label="With a note"
                          value={run.warned}
                          tone={run.warned ? "blue" : "slate"}
                        />
                      </Stack>

                      <Alert
                        severity={run.failed ? "warning" : "success"}
                        sx={{ borderRadius: 2 }}
                      >
                        <AlertTitle
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            mb: 0.25,
                          }}
                        >
                          {run.dryRun
                            ? "Checked — nothing was created"
                            : !run.created && run.skipped && !run.failed
                            ? "Already imported — nothing left to create"
                            : `${run.created} ${spec.groupNoun || "row"}${
                                run.created === 1 ? "" : "s"
                              } imported`}
                        </AlertTitle>
                        {run.dryRun
                          ? "This was a rehearsal. Press Import to create them for real."
                          : run.failed
                          ? "Fix the rows in the report below and upload the sheet again — everything that landed is skipped the second time."
                          : // A re-run of a sheet that is fully in already. The
                            // generic "every row landed" reads as a
                            // contradiction beside a count of zero.
                            !run.created && run.skipped
                          ? `Every row in this sheet was already in the app, so nothing was created and nothing was changed. ${
                              spec.groupNoun || "Row"
                            }s already here are never overwritten by an import.`
                          : isMaster
                          ? "Every row landed. Nothing was posted to the ledger — these are master records, not transactions."
                          : "Every row landed. The ledger entries were posted with it."}
                        {!!run.moved?.length && (
                          <Box sx={{ mt: 0.5, fontSize: "0.8rem" }}>
                            Voucher numbering moved on:{" "}
                            {run.moved
                              .map((m) => `${m.series} → ${m.next}`)
                              .join(", ")}
                            .
                          </Box>
                        )}
                      </Alert>

                      <Button
                        size="small"
                        startIcon={<DownloadRoundedIcon />}
                        onClick={() =>
                          downloadOutcomeReport(run.results, spec, {
                            dryRun: run.dryRun,
                          })
                        }
                        sx={{ mt: 1, textTransform: "none", fontWeight: 600 }}
                      >
                        Download the row-by-row report
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, bgcolor: "#F8FAFC" }}>
        <Button
          onClick={handleClose}
          disabled={busy}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Close
        </Button>
        {phase === "running" && (
          <Button
            onClick={() => {
              stopRef.current = true;
            }}
            color="inherit"
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Stop after this batch
          </Button>
        )}
        <Button
          onClick={() => execute(true)}
          disabled={isDraft || busy || !summary?.ready}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Check first
        </Button>
        <Tooltip
          title={
            isDraft
              ? "Not yet available — the sheet check above is live, the create step is being built"
              : ""
          }
        >
          <span>
            <Button
              variant="contained"
              disabled={isDraft || busy || !summary?.ready}
              onClick={() => execute(false)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                bgcolor: BLUE,
                "&:hover": { bgcolor: "#1D4ED8" },
              }}
            >
              {summary?.ready
                ? `Import ${summary.ready} row${summary.ready === 1 ? "" : "s"}`
                : "Import"}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default ImportSheetDialog;
